// frontend/src/services/api.js
import axios from 'axios';

// Configuration pour production HTTPS via API Gateway
const PYTHON_API_BASE_URL = 'https://certifia.ai/search-api';

/**
 * Base URL Node : si REACT_APP_NODE_API_URL est défini (y compris chaîne vide), on l’utilise.
 * Chaîne vide = même origine que le front (recommandé quand le gateway sert aussi le SPA).
 * Sinon : dev → '' + setupProxy ; prod → certifia.ai
 */
function resolveNodeApiBaseUrl() {
    const v = process.env.REACT_APP_NODE_API_URL;
    if (v !== undefined && v !== null) return v;
    return process.env.NODE_ENV === 'development' ? '' : 'https://certifia.ai';
}

const NODE_API_BASE_URL = resolveNodeApiBaseUrl();
const SCORING_API_BASE_URL = 'https://certifia.ai/scoring-api';
const DJANGO_API_BASE_URL = 'https://certifia.ai/admin-api';
const KONG_PROXY_BASE_URL = 'https://certifia.ai';  // Serveur Node.js via HTTPS Gateway (sans /api)
const SEARCH_QCM_LAB_API_BASE_URL = 'https://certifia.ai/search-api';

// Export des URLs pour utilisation dans les composants
export { SCORING_API_BASE_URL };

console.log('🔧 Configuration API - URLs de production:', {
    PYTHON_API_BASE_URL,
    NODE_API_BASE_URL,
    SCORING_API_BASE_URL,
    DJANGO_API_BASE_URL,
    SEARCH_QCM_LAB_API_BASE_URL
});

// Instance Axios pour les appels vers l'API Python (EXPORTÉE)
export const pythonApi = axios.create({
    baseURL: PYTHON_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Instance Axios via Kong Gateway (proxy unique)
export const kongApi = axios.create({
    baseURL: KONG_PROXY_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Instance Axios pour les appels vers l'API Node.js (EXPORTÉE)
export const nodeApi = axios.create({
    baseURL: NODE_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/** Même origine que nodeApi, sans intercepteurs Auth — listes publiques (pas de Bearer). */
export const publicApi = axios.create({
    baseURL: NODE_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

/** Segments d’URL pour codes d’examen (ex. BCM-SWIFT-02). */
export function encodeExamIdForUrl(examId) {
    return encodeURIComponent(String(examId || '').trim());
}

// --- Global GET de-duplication for nodeApi and pythonApi ---
const makeDedupGet = (instance) => {
    const inflight = new Map(); // key -> Promise
    const cache = new Map();    // key -> { resp, expiry }
    const originalGet = instance.get.bind(instance);
    instance.get = (url, config = {}) => {
        // Allow opting out
        if (config.__noDedup === true) {
            return originalGet(url, config);
        }
        const params = config?.params || {};
        const headers = config?.headers || {};
        const key = `${instance.defaults.baseURL || ''}${url}|p:${JSON.stringify(params)}|h:${JSON.stringify(headers)}`;
        const now = Date.now();
        const cached = cache.get(key);
        if (cached && cached.expiry > now) {
            return Promise.resolve(cached.resp);
        }
        const pending = inflight.get(key);
        if (pending) return pending;
        const p = originalGet(url, config)
            .then((resp) => {
                inflight.delete(key);
                // Short TTL cache to absorb bursty duplicates (StrictMode, concurrent mounts)
                cache.set(key, { resp, expiry: now + 1000 });
                return resp;
            })
            .catch((err) => {
                inflight.delete(key);
                throw err;
            });
        inflight.set(key, p);
        return p;
    };
};

makeDedupGet(nodeApi);
makeDedupGet(pythonApi);

// Instance Axios pour les appels vers l'API de Scoring (NOUVEAU & EXPORTÉE)
export const scoringApi = axios.create({
    baseURL: SCORING_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Instance Axios pour les appels vers l'API Django Admin (NOUVEAU & EXPORTÉE)
export const djangoApi = axios.create({
    baseURL: DJANGO_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// fetchAllInstructions pour récupérer une liste d'instructions (via Node.js)
export const fetchAllInstructions = async () => {
    try {
        const response = await nodeApi.get('/api/instructions'); // Appelle l'API Node.js
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la récupération de toutes les instructions (Node.js) :", error);
        throw error;
    }
};

// fetchLabContent pour récupérer le contenu détaillé d'une instruction (via Node.js)
export const fetchLabContent = async (instructionId) => {
    try {
        const response = await nodeApi.get(`/api/labs/${instructionId}`); // Appelle l'API Node.js
        return response.data;
    } catch (error) {
        console.error(`Erreur lors de la récupération du Lab ${instructionId} (Node.js) :`, error);
        throw error;
    }
};

// fetchRandomInstructionId pour récupérer un ID d'instruction aléatoire (via Node.js)
export const fetchRandomInstructionId = async () => {
    try {
        const response = await nodeApi.get('/api/instructions/random-id'); // Appelle l'API Node.js
        // L'API est censée renvoyer un objet comme { instruction_id: "..." }
        return response.data.instruction_id;
    } catch (error) {
        console.error("Erreur lors de la récupération d'un ID d'instruction aléatoire (Node.js) :", error);
        throw error;
    }
};

// NEW: V2-compliant function to prime the LLM context
export const primeLlmContext = async (primePayload) => {
    try {
        // Priming QCM : implémenté sur scoring-api (LLM local), pas sur search-api ni /api/prime-context (answer_evaluation).
        const response = await scoringApi.post('/api/prime-llm-context', primePayload);
        console.log(`V2 LLM Priming successful. Status: ${response.status}`);
        return response.data;
    } catch (error) {
        console.error("Error priming LLM with instruction context (V2):", error);
        throw error;
    }
};

// Prime instruction (global; stage=instruction) via Kong → llm-service
export const primeInstructionGlobal = async ({ examId, instruction_id, exam_label, technical_solution }) => {
    const payload = {
        stage: 'instruction',
        cache_strategy: 'prompt_cache',
        examId,
        instruction_id,
        exam_label: exam_label || examId,
        technical_solution: technical_solution || ''
    };
    const resp = await kongApi.post('/api/llm/prime', payload);
    return resp.data;
};

export const fetchInstructionAndPrimeLLM = async (instructionId, subInstructionId) => {
    const encInst = encodeURIComponent(instructionId);
    const encSub = encodeURIComponent(subInstructionId);
    const response = await nodeApi.get(`/api/orchestrate-instruction-load/${encInst}/${encSub}`);
    return response.data;
};

// NEW: Function to fetch priming prompt from backend configuration
export const fetchPrimingPrompt = async () => {
    try {
        const response = await pythonApi.get('/api/config/prompts');
        return response.data.primingPrompt;
    } catch (error) {
        console.error("Error fetching priming prompt from backend:", error);
        throw error; // No fallback - force error detection
    }
};

// NEW: Fetch full prompts configuration (to access IaGen_* keys)
export const fetchPromptsConfig = async () => {
    try {
        const response = await pythonApi.get('/api/config/prompts');
        return response.data;
    } catch (error) {
        console.error("Error fetching prompts configuration from backend:", error);
        throw error;
    }
};

// --- Simple in-memory caches to de-duplicate frequent GETs ---
let __authorizedExamsCache = null;
let __authorizedExamsInFlight = null;
let __authorizedExamsUserId = null;

/** Clé stable pour le cache : GET /profile Mongo renvoie souvent `_id` sans `id`. */
function stableUserKeyFromStoredProfile(u) {
    if (!u || typeof u !== 'object') return null;
    const k = u.id ?? u._id ?? u.username ?? u.email;
    const s = k != null ? String(k).trim() : '';
    return s || null;
}

/**
 * Liste des examens « extrait public » (sans JWT, via publicApi pour éviter Bearer / 401 gateway).
 * Ordre : /api/public (préfixe dédié), puis qcm, puis auth.
 */
export const fetchPublicExtractExams = async () => {
    const cfg = { params: { _: Date.now() } };
    const paths = [
        '/api/public/extract-exams',
        '/api/qcm/public/extract-exams',
        '/api/auth/public-extract-exams',
    ];
    let lastError = null;
    for (const url of paths) {
        try {
            const response = await publicApi.get(url, cfg);
            const data = response.data;
            const examens = Array.isArray(data?.examens)
                ? data.examens
                : Array.isArray(data?.exams)
                  ? data.exams
                  : null;
            if (data && data.success && examens) {
                return { success: true, examens };
            }
            lastError = new Error(data?.error || data?.message || 'Invalid response');
        } catch (err) {
            lastError = err;
        }
    }
    throw lastError || new Error('fetchPublicExtractExams failed');
};

export function formatExamPrice(cents, currency = 'eur', locale) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    try {
        return new Intl.NumberFormat(locale || undefined, {
            style: 'currency',
            currency: String(currency || 'eur').toUpperCase(),
        }).format(n / 100);
    } catch {
        return `${(n / 100).toFixed(2)} ${String(currency || 'eur').toUpperCase()}`;
    }
}

export const fetchBillingConfig = async () => {
    const response = await publicApi.get('/api/billing/config');
    return response.data;
};

export const createCheckoutSession = async (examId) => {
    const response = await nodeApi.post('/api/billing/checkout-session', { examId });
    return response.data;
};

export const requestExamInvoice = async (examId) => {
    const response = await nodeApi.post('/api/billing/request-invoice', { examId });
    return response.data;
};

export const fetchCheckoutSessionStatus = async (sessionId) => {
    const response = await nodeApi.get(`/api/billing/session/${encodeURIComponent(sessionId)}`);
    return response.data;
};

export const createBillingPortalSession = async () => {
    const response = await nodeApi.post('/api/billing/portal');
    return response.data;
};

export const fetchBillingAdminDashboard = async () => {
    const response = await nodeApi.get('/api/billing/admin/dashboard');
    return response.data;
};

export const fetchBillingAdminPurchases = async ({ page = 1, limit = 20, search = '' } = {}) => {
    const response = await nodeApi.get('/api/billing/admin/purchases', {
        params: { page, limit, search: search || undefined },
    });
    return response.data;
};

export const fetchProgressAdminDashboard = async ({ page = 1, limit = 25, search = '', examId = '' } = {}) => {
    const response = await nodeApi.get('/api/progress/admin/dashboard', {
        params: { page, limit, search: search || undefined, examId: examId || undefined },
    });
    return response.data;
};

export const runProgressSnapshotsNow = async () => {
    const response = await nodeApi.post('/api/progress/admin/run');
    return response.data;
};

export const fetchAuthorizedExams = async () => {
    let currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    let currentUserId = stableUserKeyFromStoredProfile(currentUser);

    if (!currentUserId) {
        console.warn('⚠️ fetchAuthorizedExams: Profil utilisateur non chargé, en attente...');
        await new Promise((resolve) => setTimeout(resolve, 100));
        currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        currentUserId = stableUserKeyFromStoredProfile(currentUser);
        if (!currentUserId) {
            throw new Error('Profil utilisateur non chargé. Veuillez vous reconnecter.');
        }
    }

    if (__authorizedExamsCache && __authorizedExamsUserId !== currentUserId) {
        console.log('🔄 User changed, invalidating authorized exams cache');
        __authorizedExamsCache = null;
        __authorizedExamsInFlight = null;
    }
    
    if (__authorizedExamsCache) return __authorizedExamsCache;
    if (__authorizedExamsInFlight) return __authorizedExamsInFlight;
    
    console.log('📚 Fetching authorized exams for user:', currentUserId);
    __authorizedExamsInFlight = nodeApi.get('/api/auth/examens', {
        params: {
            // Add a cache-busting parameter
            _: new Date().getTime(),
        }
    })
        .then((resp) => {
            __authorizedExamsCache = resp.data;
            __authorizedExamsUserId = currentUserId;
            __authorizedExamsInFlight = null;
            console.log('✅ Authorized exams cached for user:', currentUserId);
            return __authorizedExamsCache;
        })
        .catch((err) => {
            __authorizedExamsInFlight = null;
            throw err;
        });
    return __authorizedExamsInFlight;
};

// Fonction pour invalider le cache des examens autorisés
export const invalidateAuthorizedExamsCache = () => {
    console.log('🗑️ Invalidating authorized exams cache');
    __authorizedExamsCache = null;
    __authorizedExamsInFlight = null;
    __authorizedExamsUserId = null;
};

let __authorizedStrategiesCache = null;
let __authorizedStrategiesInFlight = null;
export const fetchAuthorizedStrategies = async () => {
    if (__authorizedStrategiesCache) return __authorizedStrategiesCache;
    if (__authorizedStrategiesInFlight) return __authorizedStrategiesInFlight;
    __authorizedStrategiesInFlight = nodeApi.get('/api/auth/strategies')
        .then((resp) => {
            __authorizedStrategiesCache = resp.data;
            __authorizedStrategiesInFlight = null;
            return __authorizedStrategiesCache;
        })
        .catch((err) => {
            __authorizedStrategiesInFlight = null;
            throw err;
        });
    return __authorizedStrategiesInFlight;
};

let __promptsConfigCache = null;
let __promptsConfigInFlight = null;
export const fetchPromptsConfigCached = async () => {
    if (__promptsConfigCache) return __promptsConfigCache;
    if (__promptsConfigInFlight) return __promptsConfigInFlight;
    __promptsConfigInFlight = pythonApi.get('/api/config/prompts')
        .then((resp) => {
            __promptsConfigCache = resp.data;
            __promptsConfigInFlight = null;
            return __promptsConfigCache;
        })
        .catch((err) => {
            __promptsConfigInFlight = null;
            throw err;
        });
    return __promptsConfigInFlight;
};

export const fetchQCMContent = async (qcmId) => {
    console.log('🔍 Fetching QCM content for ID:', qcmId);

    // Helper: try a list of candidate paths against the search API gateway
    const trySearchLoadItem = async () => {
        const payload = {
            id: qcmId,
            userid: 'anonymous_user',
            questionType: 'qcm'
        };
        const candidatePaths = [
            '/api/search/load_item',
            '/load_item',
            '/api/load_item'
        ];
        for (const path of candidatePaths) {
            try {
                const url = `${SEARCH_QCM_LAB_API_BASE_URL}${path}`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!response.ok) {
                    console.warn(`⚠️ load_item attempt failed on ${path} with status ${response.status}`);
                    continue;
                }
                const data = await response.json();
                if (data && data.success && data.item) {
                    console.log('✅ QCM content received via search load_item:', data.item);
                    return data.item;
                }
            } catch (e) {
                console.warn(`⚠️ load_item attempt threw on ${path}:`, e?.message || e);
            }
        }
        return null;
    };

    // 1) First try via Search API load_item (supports ephemeral ids like qcm_...)
    const itemViaSearch = await trySearchLoadItem();
    if (itemViaSearch) return itemViaSearch;

    // 2) Fallback to Node API by QCM id when it's a stable id (e.g., 7459823_1)
    try {
        // Heuristic: skip node fetch for ephemeral ids starting with 'qcm_'
        if (!String(qcmId).startsWith('qcm_')) {
            const nodeResp = await nodeApi.get(`/api/qcm/${encodeURIComponent(qcmId)}`);
            if (nodeResp?.data) {
                console.log('✅ QCM content received via Node API:', nodeResp.data);
                return nodeResp.data;
            }
        }
    } catch (nodeErr) {
        console.warn('⚠️ Node API /api/qcm/{id} fallback failed:', nodeErr?.message || nodeErr);
    }

    throw new Error('QCM not found via search load_item or Node API');
};

export const fetchExplanationContent = async (explanationId) => {
    try {
        console.log('🔍 Fetching explanation content for ID:', explanationId);
        // Pour l'instant, les explications sont incluses dans les données QCM
        // Nous pourrions implémenter un endpoint séparé si nécessaire
        console.log('ℹ️ Explanations are included in QCM data');
        return { message: 'Explanations are included in QCM content' };
    } catch (error) {
        console.error("Erreur lors de la récupération de l'explication :", error);
        throw error;
    }
};

export const submitQCMAnswer = async (payload) => {
    try {
        // Calls pythonApi for QCM submission if evaluation goes through Python
        const response = await pythonApi.post('/interact/qcm_answer', payload);
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la soumission de la réponse QCM :", error);
        throw error;
    }
};

// V2 evaluation function, replaces old submitTerminalCommand and fetchExplanation
export const evaluateAnswer = async (evaluationPayload) => {
    try {
        const response = await pythonApi.post('/api/evaluation', evaluationPayload);
        if (response.data.success) {
            console.log("V2 LLM evaluation successful:", response.data.evaluation);
        } else {
            console.error("V2 LLM evaluation failed:", response.data.message);
        }
        return response.data; // Expected format: { success, evaluation: { is_correct, result } }
    } catch (error) {
        console.error("Erreur lors de la soumission de la commande terminale (V2):", error);
        if (error.response) {
            console.error("API error response:", error.response.status, error.response.data);
            return { success: false, message: error.response.data.message || "Erreur du serveur", details: error.response.data.details || error.message };
        } else if (error.request) {
            console.error("No response received from server:", error.request);
            return { success: false, message: "Could not connect to evaluation server.", details: error.message };
        } else {
            console.error("Error configuring request:", error.message);
            return { success: false, message: "Unexpected error sending command.", details: error.message };
        }
    }
};

export const submitTerminalCommand = async (evaluationPayload) => {
    const data = await evaluateAnswer(evaluationPayload);
    if (!data?.evaluation || data.evaluation.is_correct === undefined) {
        return data;
    }
    const ic = data.evaluation.is_correct;
    const yes =
        ic === 'yes' ||
        ic === true ||
        (typeof ic === 'string' && ic.toLowerCase() === 'yes');
    return {
        ...data,
        evaluation: {
            ...data.evaluation,
            is_correct: yes ? 'yes' : 'no'
        }
    };
};

export const fetchExplanation = async (payload) => {
    try {
        const response = await pythonApi.post('/api/explain', {
            student_answer: payload.student_answer,
            sub_instruction_contexte:
                payload.sub_instruction_contexte ||
                payload.sub_instruction_description ||
                '',
            sub_instruction_description: payload.sub_instruction_description || ''
        });
        const d = response.data;
        if (d?.success && d.explanation != null) {
            return {
                success: true,
                evaluation: { explanation: String(d.explanation) }
            };
        }
    } catch (error) {
        console.warn('fetchExplanation /api/explain:', error?.message || error);
    }
    const data = await evaluateAnswer(payload);
    if (data?.success && data.evaluation) {
        const expl =
            data.evaluation.explanation != null
                ? String(data.evaluation.explanation)
                : data.evaluation.result != null
                    ? String(data.evaluation.result)
                    : '';
        if (expl) {
            return { success: true, evaluation: { explanation: expl } };
        }
    }
    return { success: false, evaluation: { explanation: '' } };
};

export const requestHelp = async (payload) => {
    try {
        const response = await pythonApi.post('/interact/request_help', payload);
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la demande d'aide :", error);
        throw error;
    }
};

// NEW: Function to get QCM max time from Node.js API
export const fetchQCMMaxTime = async () => {
    try {
        const response = await nodeApi.get('/api/settings/qcm-max-time');
        return response.data.maxTime; // returns the value, e.g., 45
    } catch (error) {
        console.error("Erreur lors de la récupération du temps max pour le QCM :", error);
        throw error;
    }
};

// NOUVEAU: Fonction "fire-and-forget" pour envoyer les données au service de scoring
export const submitForScoring = (payload) => {
    scoringApi.post('/api/score', payload)
        .then(response => {
            console.log('Scoring API triggered successfully:', response.data);
        })
        .catch(error => {
            console.error('Error triggering Scoring API:', error);
            // On ne "throw" pas l'erreur pour ne pas bloquer l'UI
        });
};

// NEW: Submit consolidated QCM score and metadata to backend
export const submitQcmScore = async (qcmScorePayload) => {
    try {
        const response = await scoringApi.post('/api/qcm_score', qcmScorePayload);
        console.log('✅ QCM score submitted:', response.data);
        return response.data;
    } catch (error) {
        console.error('❌ Error submitting QCM score:', error?.response?.data || error.message);
        // Do not throw to avoid breaking UI flow
        return { success: false, message: error?.response?.data || error.message };
    }
};

// NOUVEAU: Fonction pour récupérer l'instruction suivante basée sur les objectifs résiduels
export const fetchNextInstruction = async (strategyId, examId) => {
    try {
        // Utilisation via l'API Gateway HTTPS
        const response = await fetch('https://certifia.ai/api/next-instruction', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                strategy_id: strategyId,
                exam_id: examId
            })
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data.instruction_id;
    } catch (error) {
        console.error("Erreur lors de la récupération de l'instruction suivante:", error);
        throw error;
    }
};

// =============== QCM-SPECIFIC API FUNCTIONS ===============

// Note: fetchQCMContent is already defined earlier in this file

// Evaluate QCM free text answer
export const evaluateQCMAnswer = async (payload) => {
    try {
        console.log('🔥 Evaluating QCM answer:', payload);
        const response = await pythonApi.post('/api/evaluate-qcm', payload);
        return response.data;
    } catch (error) {
        console.error("Erreur lors de l'évaluation de la réponse QCM :", error);
        throw error;
    }
};

// Get random QCM ID for an exam
export const fetchRandomQCMId = async (examId) => {
    try {
        const response = await nodeApi.get(`/api/qcm/random/${encodeExamIdForUrl(examId)}`);
        return response.data.qcm_id;
    } catch (error) {
        console.error("Erreur lors de la récupération d'un QCM aléatoire :", error);
        throw error;
    }
};

// 🆕 NOUVELLE FONCTION: Récupérer le QCM suivant selon la stratégie (pour le chargement initial)
export const fetchNextQCMByStrategy = async (examId, strategy, user, questionType = 'qcm', exclude = null) => {
    try {
        console.log(`🔍 fetchNextQCMByStrategy: examId=${examId}, strategy=${strategy}, user=${user}, questionType=${questionType}`);
        
        const params = {
            strategy: strategy || 'random',
            user: user,
            questionType: questionType || 'qcm',
        };
        if (exclude) {
            params.exclude = String(exclude);
        }

        const response = await nodeApi.get(`/api/qcm/next/${encodeExamIdForUrl(examId)}`, {
            params,
            __noDedup: true,
        });
        
        if (response.data.success) {
            const qid =
                response.data.qcm?.qcm_id ||
                response.data.qcm?.FlashCard_Tips_id ||
                response.data.qcm_id;
            console.log(`✅ QCM suivant récupéré avec succès: ${qid}`);
            return qid;
        } else {
            throw new Error(response.data.message || 'Échec de la récupération du QCM suivant');
        }
    } catch (error) {
        console.error("Erreur lors de la récupération du QCM suivant par stratégie :", error);
        throw error;
    }
};

// Cached & de-duplicated version to avoid multiple random pulls
const __randomQcmInFlight = new Map(); // key: examId -> Promise
const __randomQcmCache = new Map();    // key: examId -> qcm_id
export const fetchRandomQCMIdCached = async (examId, strategy = 'Random', user = 'default_user') => {
    if (!examId) throw new Error('examId required');
    
    // 🆕 MODIFICATION: Utiliser la stratégie si elle est fournie
    if (strategy && strategy !== 'default') {
        console.log(`🎯 fetchRandomQCMIdCached: Utilisation de la stratégie ${strategy} pour ${examId}`);
        return fetchNextQCMByStrategy(examId, strategy, user);
    }
    
    // Fallback vers l'ancienne logique si pas de stratégie
    if (__randomQcmCache.has(examId)) return __randomQcmCache.get(examId);
    if (__randomQcmInFlight.has(examId)) return __randomQcmInFlight.get(examId);
    
    const p = nodeApi.get(`/api/qcm/random/${encodeExamIdForUrl(examId)}`)
        .then((resp) => {
            const id = resp.data?.qcm_id;
            if (id) __randomQcmCache.set(examId, id);
            __randomQcmInFlight.delete(examId);
            return id;
        })
        .catch((err) => {
            __randomQcmInFlight.delete(examId);
            throw err;
        });
    __randomQcmInFlight.set(examId, p);
    return p;
};

// fetchLearningStrategies pour récupérer les stratégies d'apprentissage (via Django)
export const fetchLearningStrategies = async () => {
    try {
        const response = await djangoApi.get('/api/learning-strategies/');
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la récupération des stratégies d\'apprentissage (Django) :', error);
        throw error;
    }
};

// openQuestionApi pour traiter les questions ouvertes de l'utilisateur avec streaming
export const openQuestionApiStreaming = (payload, onMessage, onError, onComplete) => {
    console.log('🔥 openQuestionApiStreaming - Sending payload:', payload);
    
    // Create URL for the API endpoint
    const url = new URL('/api/open-question/stream', SCORING_API_BASE_URL);
    
    // Create a promise that will be resolved when streaming is complete
    return new Promise((resolve, reject) => {
        // Use fetch for POST request with streaming
        fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Cache-Control': 'no-cache',
            },
            body: JSON.stringify(payload)
        })
        .then(response => {
            console.log('🔥 Response received:', response.status, response.statusText);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            
            let eventBuffer = '';

            const handleSseEvent = (eventText) => {
                if (!eventText || !eventText.trim()) return false;
                const dataLines = eventText
                    .split('\n')
                    .filter(line => line.startsWith('data:'))
                    .map(line => line.slice(5).trim());
                const payloadText = dataLines.join('\n').trim();
                if (!payloadText) return false;
                const data = JSON.parse(payloadText);
                console.log('🔥 Parsed streaming data:', data);
                if (data.type === 'error' || data.status === 'error') {
                    onError?.(data.message || 'Streaming error');
                    reject(new Error(data.message || 'Streaming error'));
                    return true;
                }
                if (data.type === 'complete' || data.status === 'completed') {
                    onComplete?.(data);
                    resolve(data);
                    return true;
                }
                onMessage?.(data);
                return false;
            };

            function readStream() {
                return reader.read().then(({ done, value }) => {
                    if (done) {
                        console.log('🔥 Stream completed');
                        onComplete?.({ success: true, completed: true });
                        resolve({ success: true, completed: true });
                        return;
                    }
                    
                    const chunk = decoder.decode(value, { stream: true });
                    console.log('🔥 Raw chunk:', chunk);

                    eventBuffer += chunk;
                    const events = eventBuffer.split('\n\n');
                    eventBuffer = events.pop() || '';

                    for (const eventText of events) {
                        try {
                            const shouldStop = handleSseEvent(eventText);
                            if (shouldStop) return;
                        } catch (e) {
                            console.error('Error parsing streaming event:', e, 'Event:', eventText);
                        }
                    }
                    
                    return readStream();
                });
            }
            
            return readStream();
        })
        .catch(error => {
            console.error('🔥 Fetch error:', error);
            onError?.(error.message || 'Connection error');
            reject(error);
        });
    });
};



// beTheTeacherApiStreaming pour l'évaluation "Be the Teacher"
export const beTheTeacherApiStreaming = (payload, onMessage, onError, onComplete) => {
    console.log('🔥 beTheTeacherApiStreaming - Sending payload:', payload);
    
    // Create URL for the API endpoint
    const url = new URL('/api/be-the-teacher', SCORING_API_BASE_URL);
    
    // Create a promise that will be resolved when streaming is complete
    return new Promise((resolve, reject) => {
        // Use fetch for POST request with streaming
        fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream',
                'Cache-Control': 'no-cache',
            },
            body: JSON.stringify(payload)
        })
        .then(response => {
            console.log('🔥 Response received:', response.status, response.statusText);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            
            function readStream() {
                return reader.read().then(({ done, value }) => {
                    if (done) {
                        console.log('🔥 Stream completed');
                        onComplete?.({ success: true, completed: true });
                        resolve({ success: true, completed: true });
                        return;
                    }
                    
                    const chunk = decoder.decode(value, { stream: true });
                    console.log('🔥 Raw chunk:', chunk);
                    
                    const lines = chunk.split('\n');
                    
                    for (const line of lines) {
                        if (line.trim().startsWith('data: ')) {
                            try {
                                const dataStr = line.trim().slice(6);
                                if (dataStr.trim()) {
                                    const data = JSON.parse(dataStr);
                                    console.log('🔥 Parsed streaming data:', data);
                                    
                                    if (data.type === 'error') {
                                        onError?.(data.message || 'Streaming error');
                                        reject(new Error(data.message || 'Streaming error'));
                                        return;
                                    }
                                    
                                    onMessage?.(data);
                                }
                            } catch (parseError) {
                                console.warn('🔥 Failed to parse streaming data:', parseError, 'Line:', line);
                            }
                        }
                    }
                    
                    return readStream();
                });
            }
            
            return readStream();
        })
        .catch(error => {
            console.error('🔥 Stream error:', error);
            onError?.(error);
            reject(error);
        });
    });
};

// openQuestionApi pour traiter les questions ouvertes de l'utilisateur (version non-streaming pour compatibilité)
export const openQuestionApi = async (payload) => {
    try {
        console.log('🔥 openQuestionApi - Sending payload:', payload);
        const response = await scoringApi.post('/api/open-question', payload);
        console.log('🔥 openQuestionApi - Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la soumission de la question ouverte:', error);
        if (error.response) {
            console.error('Réponse d\'erreur:', error.response.data);
            throw new Error(error.response.data.message || 'Erreur lors du traitement de la question');
        } else if (error.request) {
            throw new Error('Pas de réponse du serveur. Vérifiez votre connexion.');
        } else {
            throw new Error('Erreur de configuration de la requête');
        }
    }
};

// saveOpenQuestionApi pour sauvegarder une question ouverte
export const saveOpenQuestionApi = async (questionData) => {
    try {
        console.log('🔥 saveOpenQuestionApi - Saving question:', questionData);
        const response = await scoringApi.post('/api/open-question/save', questionData);
        console.log('🔥 saveOpenQuestionApi - Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la sauvegarde de la question:', error);
        if (error.response) {
            console.error('Réponse d\'erreur:', error.response.data);
            throw new Error(error.response.data.message || 'Erreur lors de la sauvegarde');
        } else if (error.request) {
            throw new Error('Pas de réponse du serveur. Vérifiez votre connexion.');
        } else {
            throw new Error('Erreur de configuration de la requête');
        }
    }
};

// getOpenQuestionHistoryApi pour récupérer l'historique des questions
export const getOpenQuestionHistoryApi = async (user = 'anonymous', limit = 50, examCode = '') => {
    try {
        console.log('🔥 getOpenQuestionHistoryApi - Getting history for user:', user, 'exam:', examCode);
        const url = `/api/open-question/history?user=${user}&limit=${limit}${examCode ? `&exam_code=${examCode}` : ''}`;
        const response = await scoringApi.get(url);
        console.log('🔥 getOpenQuestionHistoryApi - Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la récupération de l\'historique:', error);
        if (error.response) {
            console.error('Réponse d\'erreur:', error.response.data);
            throw new Error(error.response.data.message || 'Erreur lors de la récupération');
        } else if (error.request) {
            throw new Error('Pas de réponse du serveur. Vérifiez votre connexion.');
        } else {
            throw new Error('Erreur de configuration de la requête');
        }
    }
};

// getSingleOpenQuestionApi pour récupérer une seule question par son ID
export const getSingleOpenQuestionApi = async (questionId, user = 'anonymous', examCode = '') => {
    try {
        console.log('🔥 getSingleOpenQuestionApi - Getting question:', questionId, 'for user:', user);
        const url = `/api/open-question/${questionId}?user=${user}${examCode ? `&exam_code=${examCode}` : ''}`;
        const response = await scoringApi.get(url);
        console.log('🔥 getSingleOpenQuestionApi - Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la récupération de la question:', error);
        if (error.response) {
            console.error('Réponse d\'erreur:', error.response.data);
            throw new Error(error.response.data.message || 'Erreur lors de la récupération');
        } else if (error.request) {
            throw new Error('Pas de réponse du serveur. Vérifiez votre connexion.');
        } else {
            throw new Error('Erreur de configuration de la requête');
        }
    }
};

// deleteOpenQuestionApi pour supprimer une question ouverte
export const deleteOpenQuestionApi = async (questionId, user, examCode = '') => {
    try {
        console.log('🔥 deleteOpenQuestionApi - Deleting question:', questionId, 'for user:', user);
        const response = await scoringApi.delete('/api/open-question/delete', {
            data: { id: questionId, user: user, exam_code: examCode }
        });
        console.log('🔥 deleteOpenQuestionApi - Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Erreur lors de la suppression de la question:', error);
        if (error.response) {
            console.error('Réponse d\'erreur:', error.response.data);
            throw new Error(error.response.data.message || 'Erreur lors de la suppression');
        } else if (error.request) {
            throw new Error('Pas de réponse du serveur. Vérifiez votre connexion.');
        } else {
            throw new Error('Erreur de configuration de la requête');
        }
    }
};

// Session management (mobile right-menu)
export const listSessionSummariesApi = async (examId) => {
    const response = await nodeApi.get('/api/sessions/summary', { params: { examId } });
    return response.data;
};

export const createSessionApi = async ({ examId, strategyId, name }) => {
    const response = await nodeApi.post('/api/sessions', {
        examId,
        strategyId,
        name: String(name || '').trim().slice(0, 12),
    });
    return response.data;
};

export const purgeSessionApi = async (sessionId, examId) => {
    const response = await nodeApi.delete(`/api/sessions/${encodeURIComponent(sessionId)}/purge`, {
        params: { examId },
    });
    return response.data;
};

/** Éléments sélectionnés pour une session (QCM / flashcards) — repli vide si l’endpoint est absent. */
export const listSelectedItemsApi = async ({ sessionId, examId, itemType }) => {
    try {
        const response = await nodeApi.get('/api/sessions/selected-items', {
            params: { sessionId, examId, itemType },
        });
        return response.data;
    } catch (_) {
        return { items: [] };
    }
};

export const toggleSelectedItemApi = async (payload) => {
    try {
        const response = await nodeApi.post('/api/sessions/selected-items/toggle', payload);
        return response.data;
    } catch (_) {
        return { ok: false };
    }
};


/** En-têtes pour fetch() directs (scoring / search) — aligné sur le token AuthContext. */
export function authHeaders(extra = {}) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    const headers = {
        'Content-Type': 'application/json',
        ...extra,
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}

// Export de l'URL de l'API de recherche pour les composants
export { SEARCH_QCM_LAB_API_BASE_URL };
